import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import { useFieldArray, useForm } from "react-hook-form";
import { doseLabels } from "@utils/doseAndFractions";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { RemoveCircle } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";

const areDosesInOrder = (doses: (number | null)[]) => {
  const numberDoses = doses.map((dose) => dose ?? 0);
  return numberDoses.map(
    (dose, index) =>
      index === 0 ||
      !numberDoses.some((dose2, index2) => index2 < index && dose >= dose2),
  );
};

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  doseValues: (number | null)[];
  onSubmit: (doseArray: number[]) => void;
  doseUnit: string;
}

export interface InputTypes {
  doseValues: { [key: string]: number | null }[];
}

export const DoseEditDialog = (props: Props) => {
  const { open, setOpen, onSubmit, doseUnit } = props;

  const form = useForm<InputTypes>({
    values: { doseValues: props.doseValues.map((v) => ({ value: v })) },
  });
  const doseValues = useFieldArray({
    control: form.control,
    name: "doseValues",
    rules: { minLength: 1, maxLength: 4, required: true },
  });

  const submit = () => {
    const doseArray = form.getValues().doseValues.map((v) => v.value);
    const validDoses = areDosesInOrder(doseArray);

    if (validDoses.includes(false)) {
      validDoses.forEach((valid, index) => {
        if (!valid) {
          form.setError(`doseValues.${index}.value`, {
            type: "manual",
            message: "Dose value is not in order from high to low",
          });
        }
      });
      return;
    }

    onSubmit(doseArray.filter((v) => v !== null));
  };

  const addDose = async () => {
    const len = doseValues.fields.length;
    if (len <= 1) {
      doseValues.append({ value: null });
    } else {
      doseValues.insert(len - 1, { value: null });
    }
  };

  const removeDose = (index: number) => {
    doseValues.remove(index);
  };

  const handleClose = () => {
    form.reset({ doseValues: props.doseValues.map((v) => ({ value: v })) });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onKeyDown={(event) => {
        if (event.key === "Tab") {
          event.stopPropagation();
        }
      }}
    >
      <FormContainer
        formContext={form}
        handleSubmit={form.handleSubmit(submit)}
      >
        <DialogTitle>Dose</DialogTitle>

        <DialogContent style={{ paddingTop: "5px" }}>
          <Stack direction="column" spacing={2}>
            {doseValues.fields.map((item, index) => {
              return (
                <TextFieldElement
                  control={form.control}
                  name={`doseValues.${index}.value`}
                  type="number"
                  key={item.id}
                  id={`dose-${index}`}
                  label={`${
                    doseLabels[doseValues.fields.length][index]
                  } (${doseUnit})`}
                  autoFocus
                  fullWidth
                  required
                  slotProps={{
                    input: {
                      name: `doseValues.${index}.value`,
                      onChange: (event) => {
                        const value =
                          event.target.value === ""
                            ? null
                            : Number(event.target.value);
                        doseValues.update(index, { value });
                      },
                      style: { paddingRight: "4px" },
                      endAdornment: index !== 0 && (
                        <IconButton
                          data-testid={`${
                            doseLabels[doseValues.fields.length][index]
                          }-delete`}
                          onClick={() => removeDose(index)}
                        >
                          <RemoveCircle />
                        </IconButton>
                      ),
                      sx: {
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            display: "none",
                          },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                      },
                    },
                  }}
                />
              );
            })}
            <Button
              disabled={doseValues.fields.length >= 4}
              variant="outlined"
              onClick={addDose}
              startIcon={<AddIcon />}
            >
              Add dose level (SIB)
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" data-testid="submit-btn">
            Confirm
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};
